import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Select } from '../../../../../../../components/Select/index';
import { BASE_CURRENCIES_FORMPF, FORMPF_NUMERIC_TO_ISO } from '../ESMA/constants';

// Custom label component for multi-line currency display
const CurrencyLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 4px 0;
`;

const CurrencyCode = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 1.2;
`;

const CurrencyDescription = styled.div`
  font-size: 12px;
  color: #909296;
  line-height: 1.2;
  margin-top: 2px;
`;

// Parse label format: "USD - United States Dollar" -> { code: "USD", description: "United States Dollar" }
const parseCurrencyLabel = label => {
  if (!label || label === 'None' || label === '') {
    return { code: '', description: '' };
  }
  const parts = label.split(' - ');
  return {
    code: parts[0]?.trim() || '',
    description: parts[1]?.trim() || ''
  };
};

// Custom option renderer for dropdown options
const CurrencyOption = ({ label }) => {
  const { code, description } = parseCurrencyLabel(label);

  if (!code) {
    // For "None" option
    return <span>{label}</span>;
  }
  return (
    <CurrencyLabelContainer>
      <CurrencyCode>{code}</CurrencyCode>
      <CurrencyDescription>{description}</CurrencyDescription>
    </CurrencyLabelContainer>
  );
};

// Custom single value renderer for selected option
const CurrencyValue = ({ label }) => {
  const { code, description } = parseCurrencyLabel(label);

  if (!code) {
    return null;
  }
  return (
    <CurrencyLabelContainer>
      <CurrencyCode>{code}</CurrencyCode>
      <CurrencyDescription>{description}</CurrencyDescription>
    </CurrencyLabelContainer>
  );
};

const filterCurrencyOption = (option, inputValue) => {
  if (!inputValue) return true;
  const search = inputValue.toLowerCase();
  const { code, description } = parseCurrencyLabel(option.label);
  return code.toLowerCase().includes(search) || description.toLowerCase().includes(search);
};

const FormPFBaseCurrencyField = ({ fund, onChange, disabled, required }) => {
  const optionsWithPlaceholder = useMemo(
    () => [{ value: '', label: 'Select Currency' }, ...BASE_CURRENCIES_FORMPF],
    []
  );

  const selectedBaseCurrencyValue = useMemo(() => {
    const code = fund.base_currency;

    // No currency set yet: show placeholder
    if (!code) {
      return null;
    }

    // Backward compat: old numeric codes (e.g. '001') stored before the ISO migration
    if (/^\d+$/.test(code)) {
      const isoEntry = FORMPF_NUMERIC_TO_ISO.find(e => e.value === code);
      if (!isoEntry) return null;
      return BASE_CURRENCIES_FORMPF.find(opt => opt.value === isoEntry.label) || null;
    }

    // ISO code (current format) — find directly by value
    return BASE_CURRENCIES_FORMPF.find(opt => opt.value === code) || null;
  }, [fund.base_currency]);

  return (
    <Select
      required={required}
      isSearchable
      filterOption={filterCurrencyOption}
      value={selectedBaseCurrencyValue}
      options={optionsWithPlaceholder}
      getOptionValue={opt => opt.value}
      getOptionLabel={opt => opt.label}
      formatOptionLabel={opt =>
        CurrencyOption({
          label: opt.label
        })
      }
      formatSingleValue={opt =>
        CurrencyValue({
          label: opt.label
        })
      }
      onChange={onChange}
      isDisabled={disabled}
      placeholder="Select Currency"
      menuPortalTarget={document.body}
      menuPlacement="auto"
    />
  );
};

export default FormPFBaseCurrencyField;
